'use client'

import { Card, CardContent } from '@/components/card'
import { cn } from '@/utils'
import * as React from 'react'
import { IntroScreen } from './components/IntroScreen'
import { ResultScreen } from './components/ResultScreen'
import {
  SquareFragmentTaskProps,
  TaskResult,
  TaskScreen,
  TaskStage
} from './components/TaskScreen'

// Typy danych
interface SquareFragment {
  id: string
  // Współrzędne fragmentu w oryginalnym obrazku
  sourceCoords: { x: number; y: number; width: number; height: number }
  // Czy to jest poprawny fragment
  isCorrect: boolean
}

interface SquareTask {
  id: string
  title: string
  description: string
  // URL oryginalnego obrazka
  imageUrl: string
  // Pozycja i rozmiar brakującego fragmentu
  emptySpotPosition: { top: string; left: string; width: string; height: string }
  // Współrzędne brakującego fragmentu w oryginalnym obrazku
  emptySpotCoords: { x: number; y: number; width: number; height: number }
  // Fragmenty do wyboru
  fragments: SquareFragment[]
}

export function ImageMatchingTask({
  tasks: taskList,
  imageUrl,
  timeLimit = 0,
  onStart,
  onComplete,
  className,
  isDraggable = true
}: SquareFragmentTaskProps) {
  const lionTask: SquareTask = {
    id: 'lion-task',
    title: 'Dopasuj brakujący fragment obrazka lwa',
    description: 'Przeciągnij właściwy fragment na puste miejsce w obrazku.',
    imageUrl:
      imageUrl ?? 'https://images.unsplash.com/photo-1546182990-dffeafbe841d?q=80&w=1000',
    emptySpotPosition: {
      top: '30%',
      left: '40%',
      width: '20%',
      height: '20%'
    },
    emptySpotCoords: {
      x: 240,
      y: 120,
      width: 120,
      height: 80
    },
    fragments: [
      {
        id: 'fragment-1',
        sourceCoords: {
          x: 240,
          y: 120,
          width: 120,
          height: 80
        },
        isCorrect: true
      },
      {
        id: 'fragment-2',
        sourceCoords: {
          x: 100,
          y: 200,
          width: 120,
          height: 80
        },
        isCorrect: false
      },
      {
        id: 'fragment-3',
        sourceCoords: {
          x: 400,
          y: 150,
          width: 120,
          height: 80
        },
        isCorrect: false
      },
      {
        id: 'fragment-4',
        sourceCoords: {
          x: 300,
          y: 250,
          width: 120,
          height: 80
        },
        isCorrect: false
      }
    ]
  }
  const [stage, setStage] = React.useState<TaskStage>('intro')
  const [currentTaskIndex, setCurrentTaskIndex] = React.useState(0)
  const [placedFragmentId, setPlacedFragmentId] = React.useState<string | null>(null)
  const [results, setResults] = React.useState<TaskResult[]>([])
  const [remainingTime, setRemainingTime] = React.useState(timeLimit)
  const [startTime, setStartTime] = React.useState(0)
  const [isCorrect, setIsCorrect] = React.useState(false)
  const [isDragging, setIsDragging] = React.useState(false)
  const [draggedFragmentId, setDraggedFragmentId] = React.useState<string | null>(null)

  const fragmentRefs = React.useRef<Record<string, HTMLDivElement | null>>({})
  const tasks = taskList ?? [lionTask]

  const currentTask = tasks[currentTaskIndex]

  const getCorrectFragmentId = () => {
    const correctFragment = currentTask.fragments.find(
      (fragment: any) => fragment.isCorrect
    )
    return correctFragment ? correctFragment.id : ''
  }

  const startTask = () => {
    setStage('task')
    setCurrentTaskIndex(0)
    setPlacedFragmentId(null)
    setResults([])
    setRemainingTime(timeLimit)
    setStartTime(Date.now())
  }

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, fragmentId: string) => {
    e.dataTransfer.setData('fragmentId', fragmentId)
    setIsDragging(true)
    setDraggedFragmentId(fragmentId)

    const draggedElement = fragmentRefs.current[fragmentId]
    if (draggedElement) {
      const backgroundElement = draggedElement.querySelector('div') as HTMLElement
      if (backgroundElement) {
        const rect = backgroundElement.getBoundingClientRect()
        const offsetX = e.clientX - rect.left
        const offsetY = e.clientY - rect.top
        e.dataTransfer.setDragImage(backgroundElement, offsetX, offsetY)
      }
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const fragmentId = e.dataTransfer.getData('fragmentId')
    setIsDragging(false)
    setDraggedFragmentId(null)

    if (fragmentId && !placedFragmentId) {
      setPlacedFragmentId(fragmentId)
      checkAnswer(fragmentId)
    }
  }

  const handleDragEnd = () => {
    setIsDragging(false)
    setDraggedFragmentId(null)
  }

  const handleFragmentClick = (fragmentId: string) => {
    if (!isDraggable && !placedFragmentId) {
      setPlacedFragmentId(fragmentId)
      checkAnswer(fragmentId)
    }
  }

  const checkAnswer = (selectedFragmentId: string) => {
    const timeSpent = (Date.now() - startTime) / 1000
    const correctFragmentId = getCorrectFragmentId()
    const isCorrect = selectedFragmentId === correctFragmentId

    const result: TaskResult = {
      taskId: currentTask.id,
      selectedFragmentId,
      correctFragmentId,
      isCorrect,
      timeSpent
    }

    setResults([...results, result])
    setIsCorrect(isCorrect)

    setTimeout(() => {
      setStage('result')
    }, 800)
  }

  const nextTask = () => {
    if (currentTaskIndex < tasks.length - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1)
      setPlacedFragmentId(null)
      setStartTime(Date.now())
      setRemainingTime(timeLimit)
      setStage('task')
    } else {
      if (onComplete) {
        onComplete(results)
      }
      setStage('intro')
    }
  }

  React.useEffect(() => {
    if (stage !== 'task' || timeLimit <= 0 || remainingTime <= 0) return

    const timer = setInterval(() => {
      setRemainingTime((prev: any) => {
        if (prev <= 1) {
          clearInterval(timer)
          if (placedFragmentId) {
            checkAnswer(placedFragmentId)
          } else {
            const timeSpent = timeLimit
            const correctFragmentId = getCorrectFragmentId()
            const result: TaskResult = {
              taskId: currentTask.id,
              selectedFragmentId: null,
              correctFragmentId,
              isCorrect: false,
              timeSpent
            }
            setResults([...results, result])
            setIsCorrect(false)
            setStage('result')
          }
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [stage, timeLimit, remainingTime, placedFragmentId, currentTask, results])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  React.useEffect(() => {
    startTask()
  }, [])

  return (
    <Card className={cn('mx-auto w-full max-w-4xl', className)}>
      <CardContent className="p-6">
        {stage === 'intro' && (
          <IntroScreen
            formatTime={formatTime}
            onStart={startTask}
            timeLimit={timeLimit}
          />
        )}
        {stage === 'task' && (
          <TaskScreen
            currentTask={currentTask}
            currentTaskIndex={currentTaskIndex}
            draggedFragmentId={draggedFragmentId}
            formatTime={formatTime}
            fragmentRefs={fragmentRefs}
            isDraggable={isDraggable}
            isDragging={isDragging}
            onClick={handleFragmentClick}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDragStart={handleDragStart}
            onDrop={handleDrop}
            onStart={onStart}
            placedFragmentId={placedFragmentId}
            remainingTime={remainingTime}
            timeLimit={timeLimit}
            totalTasks={tasks.length}
          />
        )}
        {stage === 'result' && (
          <ResultScreen
            currentTask={currentTask}
            isCorrect={isCorrect}
            isLastTask={currentTaskIndex === tasks.length - 1}
            onNext={nextTask}
            placedFragmentId={placedFragmentId}
          />
        )}
      </CardContent>
    </Card>
  )
}
